import React, { useMemo, useRef, useState } from 'react';
import * as styles from './TagsPanel.module.scss';
import {
  Button,
  Icon,
  IconList,
  Text,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  Form,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { SidebarBody, SidebarFooter } from 'components/shared/Sidebar';
import { useUniqueID } from 'hooks/useAccessibleName';

export type Tag = {
  name: string;
  id: any;
};

type TagsStatus = 'loading' | 'error' | 'success';

export type TagsPanelProps = {
  studiesSelectedCount: number;
  tags: Tag[];
  tagsStatus?: TagsStatus;
  applyTags: (reviewId: string, tags: string[]) => Promise<void>;
  addTags: (reviewId: string, tagName: string) => Promise<Tag>;
  reviewId?: string;
};

const useFilteredTagList = (tags: Tag[], filter: string) =>
  useMemo(() => {
    if (!filter) return tags;

    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(filter.toLowerCase())
    );
  }, [tags, filter]);

// Without the review's tags, Create would offer duplicates of ones that exist
const tagsStatusMessage = {
  loading: 'Loading tags...',
  error: "Couldn't load tags. Refresh the page to try again.",
  success: undefined,
};

export const TagsPanel = ({
  tags,
  tagsStatus = 'success',
  applyTags,
  addTags,
  reviewId,
  studiesSelectedCount = 0,
}: TagsPanelProps) => {
  const hasStudiesSelected = studiesSelectedCount > 0;

  const tagInputId = useUniqueID({ postfix: 'tag-input' });
  const formId = useUniqueID({ postfix: 'tags-form' });
  const tagFieldRef = useRef<HTMLInputElement>(null);
  const [tagFieldValue, setTagFieldValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  const availableTags = useFilteredTagList(tags, tagFieldValue);

  const newTagName = tagFieldValue.trim();
  const showCreateButton =
    newTagName !== '' &&
    !tags.some((tag) => tag.name.toLowerCase() === newTagName.toLowerCase());

  const areTagsSelected = selectedTags.size > 0;
  const applyButtonText = areTagsSelected
    ? `Apply tags (${selectedTags.size})`
    : 'Apply tags';

  const createTag = async () => {
    if (!reviewId) return;

    setIsLoading(true);
    try {
      const created = await addTags(reviewId, newTagName);
      // Select what the server saved, which can differ from what was typed
      setSelectedTags((prev) => new Set([...prev, created.name]));
      setTagFieldValue('');
    } catch {
      // addTags has already shown a toast; keep the typed name to retry
    } finally {
      tagFieldRef.current?.focus();
      setIsLoading(false);
    }
  };

  const applySelectedTags = async () => {
    if (!reviewId) return;

    setIsApplying(true);
    try {
      await applyTags(reviewId, Array.from(selectedTags));
      setSelectedTags(new Set());
    } catch {
      // applyTags has already shown a toast; keep the selection to retry
    } finally {
      setIsApplying(false);
    }
  };

  const message = hasStudiesSelected
    ? tagsStatusMessage[tagsStatus]
    : 'Select one or more studies to add tags.';

  const panel = (
    <Form id={formId}>
      <fieldset className={styles.Fieldset}>
        <div aria-live="polite">
          <Stack spacing={3}>
            {message ? (
              <Text size="sm">{message}</Text>
            ) : (
              <>
                <FormControl className={styles.TagSearch}>
                  <FormLabel
                    htmlFor={tagInputId}
                    className={styles.AddTagFormLabel}
                  >
                    Search or add new tag
                  </FormLabel>
                  <Input
                    ref={tagFieldRef}
                    id={tagInputId}
                    inputType="text"
                    placeholder="Start typing..."
                    role="textbox"
                    value={tagFieldValue}
                    onChange={(event) =>
                      setTagFieldValue(event.currentTarget.value)
                    }
                    className={styles.TagInput}
                  />
                </FormControl>
                <Stack spacing={2}>
                  {availableTags.map((tag) => {
                    return (
                      <FormControl key={tag.id}>
                        <Checkbox
                          size="sm"
                          name={tag.name}
                          checked={selectedTags.has(tag.name)}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;

                            setSelectedTags((tagSet) => {
                              if (checked) {
                                tagSet.add(tag.name);
                              } else {
                                tagSet.delete(tag.name);
                              }
                              // Have to create new one because React won't re-render
                              return new Set(Array.from(tagSet));
                            });
                          }}
                        >
                          {tag.name}
                        </Checkbox>
                      </FormControl>
                    );
                  })}
                </Stack>
                {showCreateButton && (
                  <Button
                    onClick={createTag}
                    type="primary"
                    variant="ghost"
                    isLoading={isLoading}
                    className={styles.CreateTag}
                    data-pendo-key="create-tag"
                  >
                    <Icon icon={IconList.light.faPlus} size="xl" />
                    Create “{newTagName}” tag
                  </Button>
                )}
              </>
            )}
          </Stack>
        </div>
      </fieldset>
    </Form>
  );

  return (
    <>
      <SidebarBody>{panel}</SidebarBody>
      {!message && (
        <SidebarFooter>
          <Inline alignX="spaceBetween">
            <Button
              aria-live="assertive"
              type={areTagsSelected ? 'brand' : 'neutral'}
              htmlType="submit"
              // The footer sits outside the form, so link it for Enter-to-apply
              form={formId}
              className={styles.ApplyButton}
              onClick={applySelectedTags}
              isLoading={isApplying}
              disabled={isLoading || isApplying || !areTagsSelected}
              data-pendo-key="apply-tags"
            >
              {applyButtonText}
            </Button>
          </Inline>
        </SidebarFooter>
      )}
    </>
  );
};
